import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectItemProps, Response, Pagination } from '@ui/src';
import {
  CustomerType,
  TariffsLog,
  Tariff,
  CustomerProps,
  CustomMargin,
  RateSource,
} from '../../../types';
import { customersComponentApi } from '../../services/customers';

export interface CustomerTariffsState {
  tariff?: SelectItemProps;
  fees: {
    isLoading: boolean;
    requestInProgress: boolean;
    selectedFees: string[];
    sort?: {
      sortProp: string;
      sortDir: string;
    };
    selectedArchiveFees: string[];
    deleteInProgress?: boolean;
    deleteSuccess?: boolean;
    restoreInProgress?: boolean;
    restoreSuccess?: boolean;
  };
  logs: {
    isLoading: boolean;
    values?: TariffsLog[];
  };
  customerType?: CustomerType;
  customerId?: string;
  tariffsList?: Tariff[];
  riskScoreRanges: SelectItemProps[];
  searchValue?: string;
  customMargins?: CustomMargin[];
  rateSources?: RateSource[];
  customMarginsPagination?: Pagination;
  customMargin?: CustomMargin;
  defaultMargin?: CustomMargin;
}

const initialState: CustomerTariffsState = {
  fees: {
    selectedFees: [],
    selectedArchiveFees: [],
    isLoading: true,
    requestInProgress: false,
  },
  logs: {
    isLoading: true,
  },
  riskScoreRanges: [
    {
      name: 'Low',
      value: '37cff052-8cc6-4f83-a3c4-507d5086aac2',
      description: 'from 0 to 24',
    },
    {
      name: 'Medium',
      value: '0925dfc6-28db-4521-a22b-2885217a0b31',
      description: 'from 25 to 49',
    },
    {
      name: 'High',
      value: '8a3d955c-3a0e-4b61-852a-11a708d02222',
      description: 'from 50 to 100',
    },
  ],
};

export const CustomerTariffsSlice = createSlice({
  name: 'customerTariffs',
  initialState,
  reducers: {
    selectFees: (state, action: PayloadAction<string[]>) => {
      state.fees.selectedFees = action.payload;
    },
    getLogs: () => {},
    getLogsSuccess: (state, action: PayloadAction<TariffsLog[]>) => {
      state.logs.isLoading = false;
      state.logs.values = action.payload;
    },
    setRateSources: (state, action: PayloadAction<RateSource[]>) => {
      state.rateSources = action.payload;
    },
    setTariffSearchValue: (state, action: PayloadAction<string>) => {
      state.searchValue = action.payload;
    },
    clearTariffsState: (state) => {
      state.fees = {
        selectedFees: [],
        selectedArchiveFees: [],
        isLoading: true,
        requestInProgress: false,
      };
      state.customerId = undefined;
      state.customerType = undefined;
      state.fees.selectedFees = [];
      state.tariff = undefined;
      state.tariffsList = undefined;
      state.customMargins = undefined;
      state.customMarginsPagination = undefined;
    },
    clearMarginPanelState: (state) => {
      state.customMargin = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersTariffsList.matchFulfilled,
      (state, action: PayloadAction<Response<Tariff>>) => {
        state.tariffsList = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.assignTariffToCustomer.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        state.tariff = {
          name: action.payload.data.tariff.name,
          value: action.payload.data.tariffId,
        };
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersRateSources.matchFulfilled,
      (state, action: PayloadAction<Response<RateSource>>) => {
        state.rateSources = action.payload.data.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerMargin.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomMargin }>) => {
        state.customMargin = action.payload.data;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerDefaultMargin.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomMargin }>) => {
        state.defaultMargin = action.payload.data;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomMargins.matchFulfilled,
      (state, action: PayloadAction<Response<CustomMargin>>) => {
        state.customMargins = action.payload.data.edges?.map(
          (item) => item.node,
        );

        state.customMarginsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.restoreCustomMarginToDefault
        .matchFulfilled,
      (state, action: PayloadAction<{ data: CustomMargin }>) => {
        state.customMargin = action.payload.data;
      },
    );
  },
});
export const {
  selectFees,
  getLogs,
  getLogsSuccess,
  clearTariffsState,
  setTariffSearchValue,
  setRateSources,
  clearMarginPanelState,
} = CustomerTariffsSlice.actions;

export default CustomerTariffsSlice.reducer;
